import type { ReactNode } from 'react';
import { LoadingNote, ErrorNote, EmptyNote } from './states';

export type RegistryColumn<T> = { key: string; header: string; render: (row: T) => ReactNode; className?: string };

export function matchesRegistrySearch(row: Record<string, unknown>, query: string, keys: string[]): boolean {
  const q = query.toLowerCase().trim();
  if (!q) return true;
  return keys.some((k) => {
    const v = row[k];
    if (typeof v === 'string') return v.toLowerCase().includes(q);
    if (Array.isArray(v)) return v.some((x) => typeof x === 'string' && x.toLowerCase().includes(q));
    return false;
  });
}

export function RegistryTable<T>({
  columns, rows, getRowKey, search, searchKeys, rowActions, state, count, emptyText,
}: {
  columns: RegistryColumn<T>[];
  rows: T[];
  getRowKey: (row: T) => string;
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
  /** When provided with `search`, the table filters `rows` itself on these keys
   *  (single-source search — pages no longer hand-roll a per-page filter). */
  searchKeys?: string[];
  rowActions?: (row: T) => ReactNode;
  state: { loading: boolean; error: boolean };
  count?: number;
  emptyText?: string;
}) {
  const visible = search && searchKeys && search.value.trim()
    ? rows.filter((r) => matchesRegistrySearch(r as Record<string, unknown>, search.value, searchKeys))
    : rows;
  return (
    <>
      {search ? (
        <input
          data-testid="registry-search" value={search.value} onChange={(e) => search.onChange(e.target.value)}
          placeholder={search.placeholder ?? 'Search…'}
          className="searchbox"
        />
      ) : null}
      {typeof count === 'number' ? <div className="listcount">{count} total</div> : null}
      {state.loading ? <LoadingNote /> : null}
      {state.error ? <ErrorNote>Could not load.</ErrorNote> : null}
      {!state.loading && !state.error && visible.length === 0 ? <EmptyNote>{emptyText ?? 'Nothing here.'}</EmptyNote> : null}
      {visible.length > 0 ? (
        <div className="tbl-wrap">
          <table data-testid="registry-table" className="tbl">
            <thead>
              <tr>
                {columns.map((c) => <th key={c.key}>{c.header}</th>)}
                {rowActions ? <th></th> : null}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={getRowKey(row)}>
                  {columns.map((c) => <td key={c.key} className={c.className ?? ''}>{c.render(row)}</td>)}
                  {rowActions ? <td><div className="flex gap-1 whitespace-nowrap">{rowActions(row)}</div></td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
